/**
 * Advanced Token Cost System
 * Configurable token costs for all operations with dynamic calculation
 */

export interface TokenCostConfig {
    // Base costs per operation type (applied BEFORE multipliers)
    baseCosts: {
        chatMessage: number;           // Base cost for any chat message
        quizGeneration: number;         // Base cost for quiz generation
        mockExamGeneration: number;     // Base cost for mock exam
        fileAnalysis: number;           // Base cost for file analysis
        codeExecution: number;          // Base cost for code execution
        diagramGeneration: number;      // Base cost for diagram generation
    };

    // Persona base costs (added to base cost)
    personaCosts: {
        standard: number;               // Standard persona cost
        tutor: number;                  // Educator/Tutor persona cost
        professional: number;           // Professional/Lawyer persona cost
    };

    // Multipliers based on complexity
    complexityMultipliers: {
        simple: number;
        moderate: number;
        complex: number;
        veryComplex: number;
    };

    // Difficulty multipliers
    difficultyMultipliers: {
        easy: number;
        medium: number;
        hard: number;
        veryHard: number;
        cpaleLevel: number;
    };

    // Per-item costs
    perItemCosts: {
        quizQuestion: number;
        examQuestion: number;
        flashcard: number;
        practiceProblems: number;
    };

    // Feature-specific costs
    featureCosts: {
        attachmentAnalysis: number;
        thoughtProcess: number;
        stepByStepSolution: number;
        visualDiagram: number;
        codeGeneration: number;
        multiStepProblem: number;
        trickQuestion: number;
    };

    // Model multipliers
    modelMultipliers: {
        flash: number;
        pro: number;
        experimental: number;
    };

    // Attachment context tracking
    attachmentContextCosts: {
        baseAttachmentCost: number;
        perFollowUpQuestion: number;
        contextDecayFactor: number; // Reduces cost over time
    };

    // Response depth base costs (added to base cost, then multiplied)
    depthCosts: {
        concise: number;                // Cost for concise depth (-3 tokens typically)
        balanced: number;               // Cost for balanced depth (0 tokens - standard)
        detailed: number;               // Cost for detailed depth (+5 tokens)
        comprehensive: number;          // Cost for comprehensive depth (+10 tokens)
    };

    // Special formatting costs (added when detected in response)
    specialCosts: {
        table: number;                  // Cost per table generated
        codeBlock: number;              // Cost per code block
        diagram: number;                // Cost per diagram/visual
        formula: number;                // Cost per mathematical formula
        list: number;                   // Cost per formatted list (long)
    };

    // File attachment charging (per MB)
    attachmentSizeCosts: {
        enabled: boolean;
        costPerMB: number;         // Base cost per MB
        minimumCost: number;       // Minimum charge regardless of size
        maximumCost: number;       // Cap on attachment cost
        chargeOnAttach: boolean;   // Charge immediately when attached (vs when sent)
    };

    // Length-based costs (per 1000 characters)
    lengthCosts: {
        inputText: number;
        outputText: number;
    };

    // Computational problem costs
    computationalCosts: {
        baseComputationCost: number;
        perCalculationStep: number;
        complexFormula: number;
        longComputation: number; // For computations with 5+ steps
        numericalAnalysis: number;
    };

    // Thresholds
    thresholds: {
        confirmationThreshold: number; // Show confirmation if cost exceeds this
        warningThreshold: number; // Show warning if cost exceeds this
        maximumCost: number; // Maximum allowed cost per request
    };

    // Expected output character estimates by response type
    // Used for pre-emptive token estimation before sending
    outputEstimates: {
        short: number;      // Simple yes/no, definitions (~150 API tokens)
        medium: number;     // Standard explanations (~375 API tokens)
        long: number;       // Detailed responses (~750 API tokens)
        veryLong: number;   // Comprehensive guides (~1250 API tokens)
        massive: number;    // Full topic discussions (~2000 API tokens)
    };

    // Daily Limits per Tier (requests per day)
    dailyLimits: {
        standard: {
            simpleQuestions: number;        // Simple questions per day
            standardChat: number;            // Standard chat per day
            chatWithSmallAttachment: number; // Chat with 0.5MB attachment per day
            complexProblems: number;         // Complex problems per day
            advancedLargeFile: number;       // Advanced + Large file per day
        };
        premium: {
            simpleQuestions: number;
            standardChat: number;
            chatWithSmallAttachment: number;
            complexProblems: number;
            advancedLargeFile: number;
        };
        max: {
            simpleQuestions: number;
            standardChat: number;
            chatWithSmallAttachment: number;
            complexProblems: number;
            advancedLargeFile: number;
        };
    };
}

/**
 * Calculate token cost for a file attachment based on size
 */
export function calculateAttachmentCost(
    fileSizeBytes: number,
    config: TokenCostConfig = DEFAULT_TOKEN_COST_CONFIG
): number {
    if (!config.attachmentSizeCosts.enabled) {
        return 0;
    }

    const fileSizeMB = fileSizeBytes / (1024 * 1024);
    const baseCost = Math.ceil(fileSizeMB * config.attachmentSizeCosts.costPerMB);

    // Apply minimum and maximum caps
    if (baseCost < config.attachmentSizeCosts.minimumCost) {
        return config.attachmentSizeCosts.minimumCost;
    }
    if (baseCost > config.attachmentSizeCosts.maximumCost) {
        return config.attachmentSizeCosts.maximumCost;
    }

    return baseCost;
}

/**
 * Detect computational content in a message
 */
function detectComputationalContent(content: string): {
    hasComputation: boolean;
    calculationSteps: number;
    hasComplexFormula: boolean;
    isLongComputation: boolean;
    hasNumericalAnalysis: boolean;
} {
    const lowerContent = content.toLowerCase();

    // Computation keywords
    const computationKeywords = [
        'calculate', 'compute', 'solve', 'find the value', 'determine',
        'what is', 'how much', 'how many', 'evaluate', 'simplify'
    ];

    // Math operators and symbols
    const mathSymbols = /[+\-*/^=()\[\]{}√∫∑∏]/g;
    const mathSymbolCount = (content.match(mathSymbols) || []).length;

    // Number patterns
    const numberPattern = /\b\d+(\.\d+)?\b/g;
    const numberCount = (content.match(numberPattern) || []).length;

    // Step indicators
    const stepIndicators = /step \d+|\d+\)|\d+\.|first|second|third|then|next|finally/gi;
    const stepMatches = content.match(stepIndicators) || [];
    const calculationSteps = stepMatches.length;

    // Complex formula indicators
    const complexFormulaPatterns = [
        /\b(derivative|integral|limit|summation|product)\b/i,
        /\b(matrix|determinant|eigenvalue|vector)\b/i,
        /\b(logarithm|exponential|trigonometric|polynomial)\b/i,
        /[∫∑∏√]/,
        /\^\d+|\*\*\d+/ // Powers
    ];

    const hasComplexFormula = complexFormulaPatterns.some(pattern => pattern.test(content));

    // Numerical analysis indicators
    const numericalAnalysisKeywords = [
        'regression', 'correlation', 'variance', 'standard deviation',
        'probability', 'statistics', 'distribution', 'hypothesis test',
        'confidence interval', 'p-value', 'significance'
    ];

    const hasNumericalAnalysis = numericalAnalysisKeywords.some(keyword =>
        lowerContent.includes(keyword)
    );

    // Determine if it's a computation
    const hasComputationKeyword = computationKeywords.some(keyword =>
        lowerContent.includes(keyword)
    );

    const hasComputation = (
        hasComputationKeyword ||
        (mathSymbolCount >= 3 && numberCount >= 2) ||
        hasComplexFormula ||
        hasNumericalAnalysis
    );

    const isLongComputation = calculationSteps >= 5;

    return {
        hasComputation,
        calculationSteps,
        hasComplexFormula,
        isLongComputation,
        hasNumericalAnalysis
    };
}

// ==================== PROFITABLE TOKEN COST CONFIG v5.0 - GEMINI API ALIGNED ====================
// Based on actual Gemini API pricing with profit margin
//
// GEMINI API FACTS (from official docs):
// - Text: ~4 characters = 1 API token
// - Output tokens cost 4x more than input tokens
// - Images: 258 API tokens (small <=384px) or 258 per 768x768 tile
// - Video: 263 API tokens per second
// - Audio: 32 API tokens per second
//
// OUR LUCA TOKEN SYSTEM (Profitable):
// - 1 Luca token ≈ 15-20 actual API tokens (accounting for input/output ratio)
// - Average message (100 char input, 600 char output):
//   * API: ~25 input tokens + ~150 output tokens = 175 API tokens
//   * Weighted cost (output 4x): 25 + (150×4) = 625 cost units
//   * We charge: 40-50 Luca tokens
//   * Profit margin: ~30-40%
//
// Quality Tiers with API alignment:
// - Quick Answer: 20-30 tokens (simple lookups, 300-400 API tokens)
// - Standard: 40-60 tokens (explanations, 600-900 API tokens)
// - Detailed: 80-120 tokens (step-by-step, 1,200-1,800 API tokens)
// - Expert: 150-250 tokens (professional analysis, 2,500-4,000 API tokens)
export const DEFAULT_TOKEN_COST_CONFIG: TokenCostConfig = {
    baseCosts: {
        chatMessage: 35,             // Base chat (aligned with API costs + 35% profit)
        quizGeneration: 120,         // Quiz generation (~1,800 API tokens)
        mockExamGeneration: 280,     // Exam generation (~4,200 API tokens)
        fileAnalysis: 50,            // File analysis (PDF = 258+ API tokens)
        codeExecution: 40,           // Code generation
        diagramGeneration: 35        // Diagram generation
    },
    complexityMultipliers: {
        simple: 0.7,                 // Quick answers (-30%)
        moderate: 1.0,               // Standard baseline
        complex: 1.8,                // Detailed (+80%)
        veryComplex: 3.5             // Expert (+250%, requires extensive output)
    },
    difficultyMultipliers: {
        easy: 0.9,                   // Easy topics (-10%)
        medium: 1.0,                 // Standard
        hard: 1.25,                  // Hard topics (+25%)
        veryHard: 1.5,               // Very hard (+50%)
        cpaleLevel: 1.8              // CPALE-level (+80%)
    },
    perItemCosts: {
        quizQuestion: 12,            // Per quiz question
        examQuestion: 18,            // Per exam question (more complex)
        flashcard: 8,                // Per flashcard
        practiceProblems: 15         // Per practice problem
    },
    featureCosts: {
        attachmentAnalysis: 65,      // Attachment analysis (increased to compensate for removed daily limit)
        thoughtProcess: 15,          // Extended thinking display
        stepByStepSolution: 20,      // Step-by-step breakdown
        visualDiagram: 30,           // Visual generation
        codeGeneration: 35,          // Code blocks
        multiStepProblem: 25,        // Multi-step problem solving
        trickQuestion: 12            // Trick question analysis
    },
    modelMultipliers: {
        flash: 1.0,                  // Standard model
        pro: 1.5,                    // Pro model (+50%)
        experimental: 1.3            // Experimental (+30%)
    },
    attachmentContextCosts: {
        baseAttachmentCost: 35,      // Base for keeping attachment in context
        perFollowUpQuestion: 12,     // Per follow-up about attachment
        contextDecayFactor: 0.85     // 15% reduction per follow-up
    },
    personaCosts: {
        standard: 0,                 // No extra cost for standard persona
        tutor: 5,                    // +5 tokens for educator mode
        professional: 10             // +10 tokens for professional mode
    },
    depthCosts: {
        concise: -3,                 // -3 tokens for brief responses
        balanced: 0,                 // No extra cost for balanced
        detailed: 5,                 // +5 tokens for detailed responses
        comprehensive: 10            // +10 tokens for comprehensive responses
    },
    specialCosts: {
        table: 15,                   // 15 tokens per table generated
        codeBlock: 10,               // 10 tokens per code block
        diagram: 20,                 // 20 tokens per diagram
        formula: 8,                  // 8 tokens per math formula
        list: 3                      // 3 tokens per long formatted list
    },
    attachmentSizeCosts: {
        enabled: true,               // Enable attachment size-based charging
        costPerMB: 35,               // 35 Luca tokens per MB - increased for removed daily limit
        minimumCost: 15,             // Minimum 15 tokens per attachment - increased for accuracy funding
        maximumCost: 150,            // Cap at 150 tokens per file (large PDFs)
        chargeOnAttach: true         // Charge when file is attached (not on send)
    },
    lengthCosts: {
        inputText: 3,                // Per 1000 chars input (~250 API tokens × profit margin)
        outputText: 12               // Per 1000 chars output (output costs 4x more in API)
    },
    computationalCosts: {
        baseComputationCost: 18,     // Base for any computation
        perCalculationStep: 8,       // Per step in calculation
        complexFormula: 25,          // Complex formulas
        longComputation: 45,         // Long multi-step computations
        numericalAnalysis: 35        // Statistical/numerical analysis
    },
    thresholds: {
        confirmationThreshold: 250,  // Confirm above 250 tokens
        warningThreshold: 500,       // Warn above 500 tokens
        maximumCost: 800             // Max per single request
    },
    outputEstimates: {
        short: 600,           // Simple yes/no, definitions (~150 API tokens)
        medium: 1500,         // Standard explanations (~375 API tokens)
        long: 3000,           // Detailed responses (~750 API tokens)
        veryLong: 5000,       // Comprehensive guides (~1250 API tokens)
        massive: 8000         // Full topic discussions (~2000 API tokens)
    },
    dailyLimits: {
        standard: {
            simpleQuestions: 100,        // ~20 tokens each = 2,000 tokens
            standardChat: 75,             // ~45 tokens each = 3,375 tokens
            chatWithSmallAttachment: 40,  // ~65 tokens each = 2,600 tokens
            complexProblems: 25,          // ~120 tokens each = 3,000 tokens
            advancedLargeFile: 15         // ~200 tokens each = 3,000 tokens
        },
        premium: {
            simpleQuestions: 300,        // ~6,000 tokens
            standardChat: 200,            // ~9,000 tokens
            chatWithSmallAttachment: 120, // ~7,800 tokens
            complexProblems: 80,          // ~9,600 tokens
            advancedLargeFile: 50         // ~10,000 tokens
        },
        max: {
            simpleQuestions: 1000,       // ~20,000 tokens
            standardChat: 600,            // ~27,000 tokens
            chatWithSmallAttachment: 400, // ~26,000 tokens
            complexProblems: 250,         // ~30,000 tokens
            advancedLargeFile: 150        // ~30,000 tokens
        }
    }
};

export interface CostCalculationInput {
    operationType: keyof TokenCostConfig['baseCosts'];
    itemCount?: number;
    inputLength?: number;
    estimatedOutputLength?: number;
    complexity?: keyof TokenCostConfig['complexityMultipliers'];
    difficulty?: keyof TokenCostConfig['difficultyMultipliers'];
    model?: keyof TokenCostConfig['modelMultipliers'];
    features?: (keyof TokenCostConfig['featureCosts'])[];
    attachmentCount?: number;
    hasActiveAttachmentContext?: boolean; // If previous message had attachments
    attachmentFollowUpCount?: number; // Number of follow-up questions about attachments
    messageContent?: string; // Message content for computational detection
}

export interface CostBreakdown {
    baseCost: number;
    itemCost: number;
    lengthCost: number;
    complexityMultiplier: number;
    difficultyMultiplier: number;
    modelMultiplier: number;
    featureCost: number;
    totalCost: number;
    breakdown: {
        label: string;
        value: number;
        isMultiplier?: boolean;
    }[];
    requiresConfirmation: boolean;
    requiresWarning: boolean;
    exceedsMaximum: boolean;
}

/**
 * Calculate token cost for any operation
 */
export function calculateTokenCost(
    input: CostCalculationInput,
    config: TokenCostConfig = DEFAULT_TOKEN_COST_CONFIG
): CostBreakdown {
    const breakdown: CostBreakdown['breakdown'] = [];

    // 1. Base cost
    const baseCost = config.baseCosts[input.operationType];
    breakdown.push({ label: 'Base Cost', value: baseCost });

    // 2. Item-based cost
    let itemCost = 0;
    if (input.itemCount && input.itemCount > 0) {
        let perItemCost = 0;

        // Determine per-item cost based on operation type
        if (input.operationType === 'quizGeneration') {
            perItemCost = config.perItemCosts.quizQuestion;
        } else if (input.operationType === 'mockExamGeneration') {
            perItemCost = config.perItemCosts.examQuestion;
        }

        itemCost = perItemCost * input.itemCount;
        if (itemCost > 0) {
            breakdown.push({
                label: `Items (${input.itemCount} × ${perItemCost})`,
                value: itemCost
            });
        }
    }

    // 3. Length-based cost
    let lengthCost = 0;
    if (input.inputLength) {
        const inputCost = Math.ceil((input.inputLength / 1000) * config.lengthCosts.inputText);
        lengthCost += inputCost;
        breakdown.push({
            label: `Input Length (${input.inputLength} chars)`,
            value: inputCost
        });
    }
    if (input.estimatedOutputLength) {
        const outputCost = Math.ceil((input.estimatedOutputLength / 1000) * config.lengthCosts.outputText);
        lengthCost += outputCost;
        breakdown.push({
            label: `Est. Output Length (${input.estimatedOutputLength} chars)`,
            value: outputCost
        });
    }

    // 4. Feature costs
    let featureCost = 0;
    if (input.features && input.features.length > 0) {
        input.features.forEach(feature => {
            const cost = config.featureCosts[feature];
            featureCost += cost;
            breakdown.push({
                label: `Feature: ${feature.replace(/([A-Z])/g, ' $1').trim()}`,
                value: cost
            });
        });
    }

    // Add attachment cost with context tracking
    if (input.attachmentCount && input.attachmentCount > 0) {
        const attachmentCost = config.featureCosts.attachmentAnalysis * input.attachmentCount;
        featureCost += attachmentCost;
        breakdown.push({
            label: `Attachments (${input.attachmentCount})`,
            value: attachmentCost
        });
    }

    // Add follow-up attachment context cost (prevents loophole)
    if (input.hasActiveAttachmentContext && input.attachmentFollowUpCount && input.attachmentFollowUpCount > 0) {
        const decayFactor = Math.pow(config.attachmentContextCosts.contextDecayFactor, input.attachmentFollowUpCount - 1);
        const followUpCost = Math.floor(config.attachmentContextCosts.perFollowUpQuestion * decayFactor);
        featureCost += followUpCost;
        breakdown.push({
            label: `Attachment Context (Follow-up ${input.attachmentFollowUpCount})`,
            value: followUpCost
        });
    }

    // 4.5. Computational problem detection and costs
    let computationalCost = 0;
    if (input.messageContent) {
        const computationIndicators = detectComputationalContent(input.messageContent);

        if (computationIndicators.hasComputation) {
            computationalCost += config.computationalCosts.baseComputationCost;
            breakdown.push({
                label: 'Computational Problem Detected',
                value: config.computationalCosts.baseComputationCost
            });
        }

        if (computationIndicators.calculationSteps > 0) {
            const stepCost = computationIndicators.calculationSteps * config.computationalCosts.perCalculationStep;
            computationalCost += stepCost;
            breakdown.push({
                label: `Calculation Steps (${computationIndicators.calculationSteps})`,
                value: stepCost
            });
        }

        if (computationIndicators.hasComplexFormula) {
            computationalCost += config.computationalCosts.complexFormula;
            breakdown.push({
                label: 'Complex Formula Detected',
                value: config.computationalCosts.complexFormula
            });
        }

        if (computationIndicators.isLongComputation) {
            computationalCost += config.computationalCosts.longComputation;
            breakdown.push({
                label: 'Long Computation (5+ steps)',
                value: config.computationalCosts.longComputation
            });
        }

        if (computationIndicators.hasNumericalAnalysis) {
            computationalCost += config.computationalCosts.numericalAnalysis;
            breakdown.push({
                label: 'Numerical Analysis Required',
                value: config.computationalCosts.numericalAnalysis
            });
        }
    }

    // Calculate subtotal before multipliers
    const subtotal = baseCost + itemCost + lengthCost + featureCost + computationalCost;

    // 5. Complexity multiplier
    const complexityMultiplier = input.complexity
        ? config.complexityMultipliers[input.complexity]
        : 1.0;
    if (complexityMultiplier !== 1.0) {
        breakdown.push({
            label: `Complexity (${input.complexity})`,
            value: complexityMultiplier,
            isMultiplier: true
        });
    }

    // 6. Difficulty multiplier
    const difficultyMultiplier = input.difficulty
        ? config.difficultyMultipliers[input.difficulty]
        : 1.0;
    if (difficultyMultiplier !== 1.0) {
        breakdown.push({
            label: `Difficulty (${input.difficulty})`,
            value: difficultyMultiplier,
            isMultiplier: true
        });
    }

    // 7. Model multiplier
    const modelMultiplier = input.model
        ? config.modelMultipliers[input.model]
        : 1.0;
    if (modelMultiplier !== 1.0) {
        breakdown.push({
            label: `Model (${input.model})`,
            value: modelMultiplier,
            isMultiplier: true
        });
    }

    // Calculate total cost
    const totalMultiplier = complexityMultiplier * difficultyMultiplier * modelMultiplier;
    const totalCost = Math.ceil(subtotal * totalMultiplier);

    // Add final total to breakdown
    breakdown.push({
        label: 'Total Cost',
        value: totalCost
    });

    return {
        baseCost,
        itemCost,
        lengthCost,
        complexityMultiplier,
        difficultyMultiplier,
        modelMultiplier,
        featureCost,
        totalCost,
        breakdown,
        requiresConfirmation: totalCost >= config.thresholds.confirmationThreshold,
        requiresWarning: totalCost >= config.thresholds.warningThreshold,
        exceedsMaximum: totalCost > config.thresholds.maximumCost
    };
}

/**
 * Analyze request complexity automatically
 */
export function analyzeComplexity(text: string): keyof TokenCostConfig['complexityMultipliers'] {
    const length = text.length;
    const hasMultipleQuestions = (text.match(/\?/g) || []).length > 2;
    const hasLists = /\d+\.|•|-/.test(text);
    const hasCode = /```|`/.test(text);
    const hasFormulas = /\$|=|∑|∫/.test(text);
    const hasTables = /\|.*\|/.test(text);

    let complexityScore = 0;

    if (length > 1000) complexityScore += 2;
    else if (length > 500) complexityScore += 1;

    if (hasMultipleQuestions) complexityScore += 1;
    if (hasLists) complexityScore += 1;
    if (hasCode) complexityScore += 2;
    if (hasFormulas) complexityScore += 1;
    if (hasTables) complexityScore += 1;

    if (complexityScore >= 5) return 'veryComplex';
    if (complexityScore >= 3) return 'complex';
    if (complexityScore >= 1) return 'moderate';
    return 'simple';
}

/**
 * Detect features in user message
 */
export function detectFeatures(text: string, hasAttachments: boolean): (keyof TokenCostConfig['featureCosts'])[] {
    const features: (keyof TokenCostConfig['featureCosts'])[] = [];

    if (hasAttachments) features.push('attachmentAnalysis');
    if (/step.?by.?step|explain|how/i.test(text)) features.push('stepByStepSolution');
    if (/diagram|chart|graph|visual/i.test(text)) features.push('visualDiagram');
    if (/code|program|function|algorithm/i.test(text)) features.push('codeGeneration');
    if (/think|reason|thought/i.test(text)) features.push('thoughtProcess');

    return features;
}

/**
 * Load token cost config from localStorage or use default
 */
export function loadTokenCostConfig(): TokenCostConfig {
    try {
        const stored = localStorage.getItem('ledger_token_cost_config');
        if (stored) {
            const parsed = JSON.parse(stored);
            // Merge with defaults to ensure all properties exist
            return {
                ...DEFAULT_TOKEN_COST_CONFIG,
                ...parsed,
                baseCosts: { ...DEFAULT_TOKEN_COST_CONFIG.baseCosts, ...parsed.baseCosts },
                complexityMultipliers: { ...DEFAULT_TOKEN_COST_CONFIG.complexityMultipliers, ...parsed.complexityMultipliers },
                difficultyMultipliers: { ...DEFAULT_TOKEN_COST_CONFIG.difficultyMultipliers, ...parsed.difficultyMultipliers },
                perItemCosts: { ...DEFAULT_TOKEN_COST_CONFIG.perItemCosts, ...parsed.perItemCosts },
                featureCosts: { ...DEFAULT_TOKEN_COST_CONFIG.featureCosts, ...parsed.featureCosts },
                modelMultipliers: { ...DEFAULT_TOKEN_COST_CONFIG.modelMultipliers, ...parsed.modelMultipliers },
                lengthCosts: { ...DEFAULT_TOKEN_COST_CONFIG.lengthCosts, ...parsed.lengthCosts },
                thresholds: { ...DEFAULT_TOKEN_COST_CONFIG.thresholds, ...parsed.thresholds },
                outputEstimates: { ...DEFAULT_TOKEN_COST_CONFIG.outputEstimates, ...parsed.outputEstimates }
            };
        }
    } catch (error) {
        console.error('Failed to load token cost config:', error);
    }
    return DEFAULT_TOKEN_COST_CONFIG;
}

/**
 * Save token cost config to localStorage
 */
export function saveTokenCostConfig(config: TokenCostConfig): void {
    try {
        localStorage.setItem('ledger_token_cost_config', JSON.stringify(config));
        console.log('✅ Token cost configuration saved');
    } catch (error) {
        console.error('Failed to save token cost config:', error);
    }
}
